import React, { useEffect, useState } from 'react';
import { appMessage as message } from '../utils/appMessage';
import { CardPageProvider, useCardPage } from '../contexts/CardPageContext';
import { StockPageProvider, useStockPage } from '../contexts/StockPageContext';
import { ProductRegistrationJobProvider, useProductRegistrationJob } from '../contexts/ProductRegistrationJobContext';
import { ADMIN_ONLY_PAGES, AppSidebar, SidebarPage } from '../components/appSidebar/AppSidebar';
import { JobProgressBar } from '../components/jobProgressBar/JobProgressBar';
import { DailyWorkflowPage, StorePriceRequestPage } from '../pages/DailyWorkflowPage';
import { StoreProductManagementPage } from '../pages/StoreProductManagementPage';
import { BuybackPage } from '../pages/BuybackPage';
import { BuybackListPage } from '../pages/BuybackListPage';
import { NaverSyncLogPage } from '../pages/NaverSyncLogPage';
import { ProductRegistrationPage } from '../pages/ProductRegistrationPage';
import { SuperOverviewPage } from '../pages/SuperOverviewPage';
import { SettingsPage } from '../pages/SettingsPage';
import { DecidedPricePage } from '../pages/DecidedPricePage';
import { useStorage } from '../contexts/StorageContext';
import { NavigationProvider } from '../contexts/NavigationContext';
import { useBuybackAlarm } from '../hooks/UseBuybackAlarm';
import { PRICE_VIEW_PAGE, readPriceView } from '../components/priceViews/priceViews';

// ─── 페이지 컴포넌트 맵 ───────────────────────────────────────────────────────
//
// 새 페이지 추가 시 여기에만 등록하면 된다.
//   1. AppSidebar.tsx 의 SidebarPage union 에 키 추가
//   2. AppSidebar.tsx 의 NAV_GROUPS 에 NavItem 추가
//   3. 여기 PAGE_COMPONENTS 에 { key: 컴포넌트 } 추가
//
// Context 공유 범위:
//   - CardPageProvider 안에 등록된 페이지는 카드 데이터·선택·편집 상태를 공유한다.
//   - 카드 데이터와 무관한 페이지(통계, 설정 등)는 Provider 밖으로 빼도 된다.
//   - 그 경우 PAGE_COMPONENTS_OUTSIDE 를 별도로 관리하면 된다.
//
const PAGE_COMPONENTS: Record<SidebarPage, React.FC> = {
  daily: DailyWorkflowPage,
  storePrice: StorePriceRequestPage, // 매장가 요청 (PriceHub 변경 · 분류 카드, 감사 #117)
  decidedPrice: DecidedPricePage, // 판매가 결정 (판매처 목록 보고 판매가 정하기, 감사 #341)
  productRegistration: ProductRegistrationPage, // 상품 등록 (신규 확장팩 일괄 등록, 원피스만)
  storeProducts: StoreProductManagementPage, // 스토어 상품 관리 (가격·재고 통합 편집)
  buyback: BuybackPage,      // 매입 관리 (고객 안내 앱에서 접수한 문의 목록)
  buybackList: BuybackListPage, // 매입 리스트 (직원이 선정한 매입 희망 상품, A4 인쇄)
  syncLog: NaverSyncLogPage, // 반영 로그 (Naver 가격·재고 실제 PUT 이력)
  settings: SettingsPage,    // 매장 설정 (PriceHub 연결 · 상품 등록 기본값)
  // 추가 예시:
  // stats:    StatsPage,
};

// ─── MainApp ──────────────────────────────────────────────────────────────────

/**
 * 로그인 후 메인 앱 셸.
 *
 * 역할:
 *   - AppSidebar 로 현재 페이지를 전환한다 (React Router 없이 state 관리).
 *   - CardPageProvider 를 한 번 감싸 내부 페이지들이 카드 데이터를 공유한다.
 *   - 실제 페이지 컴포넌트는 PAGE_COMPONENTS 맵에서 관리한다.
 *
 * 파일 위치: src/app/MainApp.tsx
 */
/**
 * 재고 동기화/반영 잡의 진행바.
 * StockPageProvider 가 MainApp 최상위에 있으므로, 페이지를 옮겨도 잡 상태(activeJobId)와
 * 폴링이 유지된다 → 재고 동기화 중에 카드 목록/오늘의 작업으로 이동해도 취소되지 않는다.
 */
const StockJobBar: React.FC = () => {
  const { activeJobId, handleJobComplete } = useStockPage();
  return <JobProgressBar jobId={activeJobId} onComplete={handleJobComplete} onOpenDetail={() => {}} />;
};

/**
 * 카드 잡(수정 가격 조회 / 판매 상태 조회 / Naver 반영)의 진행바.
 * CardPageProvider 가 MainApp 최상위에 있으므로, 카드 목록 페이지가 아닌 다른 페이지로
 * 이동해도 잡 상태(activeJobId)와 폴링이 유지된다 → 재고/매입 관리 페이지에서도 보인다.
 * 결과 모달(JobModal) 오픈 여부는 CardListPage 에서 resultModalOpen 을 읽어 처리한다.
 */
const CardJobBar: React.FC = () => {
  const { jobs, onCardJobComplete, setResultModalOpen } = useCardPage();
  return (
    <JobProgressBar
      jobId={jobs.activeJobId}
      onComplete={onCardJobComplete}
      onOpenDetail={() => setResultModalOpen(true)}
    />
  );
};

/**
 * "상품 등록" 잡의 진행바. ProductRegistrationJobProvider 가 MainApp 최상위에 있으므로,
 * 등록 도중 다른 페이지(반영 로그 등)로 이동해도 잡 상태(activeJobId)와 폴링이 유지된다
 * (2026-08-20, StockJobBar/CardJobBar와 같은 이유 — 사용자가 실사용 중 "페이지 이동했다
 * 돌아오면 진행바가 사라진다"고 발견).
 */
const RegisterJobBar: React.FC = () => {
  const { activeJobId, handleComplete } = useProductRegistrationJob();
  return <JobProgressBar jobId={activeJobId} jobType="REGISTER_PRODUCTS" onComplete={handleComplete} onOpenDetail={() => {}} />;
};

export const MainApp: React.FC = () => {
  const { isAdmin, isSuper, superStore } = useStorage();
  // 주소에 저장 필터 칩(?view=)이 있으면 그 칩의 메뉴로 연다 — 링크로 받은 보기 · 새로고침(가격 목록 개편 PR 3, 감사 card-controltower#697).
  const [requestedPage, setPage] = useState<SidebarPage>(() => {
    const v = readPriceView();
    return v ? PRICE_VIEW_PAGE[v] : 'daily';
  });
  // 조회 전용(VIEWER)에게 관리자 전용 화면(설정 · 판매가 결정 · 상품 등록 · 매입)은 그리지 않는다 — 메뉴는 이미 숨겼지만
  // 다른 길(nav.go · 남은 상태)로 열리면 첫 조회부터 403 이 두 번 떴다(감사 card-controltower#672). 역할은 로그인 응답의 role.
  const blockedPage = !isAdmin && ADMIN_ONLY_PAGES.has(requestedPage);
  const page: SidebarPage = blockedPage ? 'daily' : requestedPage;
  // 말없이 옮기면 눌렀는데 아무 일도 없는 것처럼 보인다(감사 #829) — 한 줄 알리고 요청 화면도 가격 조회로 되돌린다(다시 안 뜨게).
  useEffect(() => {
    if (!blockedPage) return;
    message.info({ key: 'admin-only-page', content: '관리자 전용 화면이라 가격 조회로 이동했습니다', duration: 3 });
    setPage('daily');
  }, [blockedPage]);

  const PageComponent = PAGE_COMPONENTS[page];
  // SUPER 가 매장을 고르지 않은 상태(superStore=null)면 전 매장 횡단 개요를 보여 준다.
  // 설정 페이지는 예외 — 매장을 고르지 않아도 열려서 "매장을 먼저 선택하세요"를 안내한다.
  const showOverview = isSuper && superStore === null && page !== 'settings';

  // 신규 매입 알람(스태프용). 매입을 볼 수 있는 컨텍스트에서만 폴링한다 — ADMIN/SUPER 이고
  // (SUPER 는 전 매장 개요가 아니라 특정 매장을 고른 상태). VIEWER 는 매입 접근이 없어 제외.
  const pendingBuyback = useBuybackAlarm(isAdmin && !showOverview, () => setPage('buyback'));

  // superStore 를 key 로 걸어, SUPER 가 대상 매장을 바꾸면 Provider 가 리마운트되며
  // 새 매장 데이터를 다시 불러온다(카드/재고 상태도 매장별로 초기화). 매장 계정은 'own' 고정.
  const scopeKey = superStore ?? 'own';

  return (
    <NavigationProvider value={{ page, go: setPage }}>
      <CardPageProvider key={scopeKey}>
        <StockPageProvider>
          <ProductRegistrationJobProvider>
            <StockJobBar />
            <CardJobBar />
            <RegisterJobBar />
            <AppSidebar current={page} onChange={setPage} badges={{ buyback: pendingBuyback }} />
            {showOverview ? <SuperOverviewPage /> : <PageComponent />}
          </ProductRegistrationJobProvider>
        </StockPageProvider>
      </CardPageProvider>
    </NavigationProvider>
  );
};
